import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useCreateNewSystemMutation,
  useGetConfigMutation,
  useLazyGetSystemQuery,
  useUpdateSystemSettingsMutation,
} from 'app/services/system.service'
import { icons } from 'utils/icons'
import SettingsHeader from 'features/settings/SettingsHeader'
import { systemActions } from 'app/slices/system.slice'
import {
  BUTTONS,
  EXPLANATIONS,
  FORM,
  NAVIGATION,
  OTHERS,
  PLACEHOLDERS,
  TABLE,
} from 'locales/locales'
import { requiredValidation, mobileVersionValidation } from 'utils/helpers/validations'
import colors from 'utils/colors'

import UploadImage from 'components/input/uplaod-input/UploadImage'
import CustomButton from 'components/button/CustomButton'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import CustomText from 'components/text/CustomText'
import Loading from 'components/spinner/Loading'
import { SUPPORTED_FILE_TYPES } from 'utils/constants'
import Checkbox from 'components/checkbox/Checkbox'
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout'
import { otrActions } from 'app/slices/otr.slice'
import { useCloseOtrMutation } from 'app/services/otrService'
import { setDataToDB } from 'utils/helpers/storageHelper'
import { IRolePermissions, SettingsPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import { FixMeLater } from 'types'
import InAppBrowserSettings, { InAppBrowser } from './InAppBrowser'
import { Input, TextArea } from '@progress/kendo-react-inputs'
import { useLogoUrl } from 'utils/hooks/useLogoUrl'

interface SystemSettings {
  name: string
  licence: string
  apiKey: string
  iosVersion: string
  androidVersion: string
  inputMaxChar: number
  imageUrlFavicon: string
  imageUrlLogo: string
  fileExtensions: string[]
  inAppBrowserTitle:string;
  inAppBrowsers: Array<InAppBrowser>
  privacyText: string
  features: {
    newsFeedEditAndDeleteEnabled: boolean
    videoCallEnabled: boolean
    fcmDetailEnabled: boolean
    fcmUserDetailEnabled: boolean
    captchaEnabled: boolean
    passwordChangeEnabled: boolean
    passwordResetEnabled: boolean
    groupWithMembers: boolean
    isGroupMembersVideoCallEnabled: boolean
    registerEnabled: boolean
    isGroupMembersEnabled: boolean
    otrEnabled: boolean
    mfaEnabled: boolean
    notificationSound: boolean
    showUnpermittedFeatures: boolean
    createTeam: boolean
    createChannel: boolean
    isShowAllTeamsForConference: boolean
    showPhoneInfo: boolean
    isPrivacyTextEnabled: boolean
  }
}

interface FeatureCheckboxProps {
  title: string
  featureKey: keyof SystemSettings['features']
  label: string
  explanation: string
}
const SystemManagement = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [createNewSystem] = useCreateNewSystemMutation()
  const [updateSystem] = useUpdateSystemSettingsMutation()
  const [getSystemSettings] = useLazyGetSystemQuery()
  const [closeOtr] = useCloseOtrMutation()
  const [config] = useGetConfigMutation()

  const systemSettingsSlice = useAppSelector((state) => state.system.data)
  const existOtrRooms = useAppSelector((state) => state.otr)
  const [systemSettingId, setSystemSettingId] = useState('')
  const [errorText, setErrorText] = useState({
    iosVersionErrorText: '',
    androidVersionErrorText: '',
    nameErrorText: '',
  })

  const [systemSettings, setSystemSettings] = useState({
    name: '',
    licence: '',
    apiKey: '',
    iosVersion: '1.1.0',
    androidVersion: '1.1.0',
    inputMaxChar: '',
    imageUrlFavicon: '',
    imageUrlLogo: '',
    fileExtensions: [''],
    inAppBrowserTitle: '',
    inAppBrowsers: [] as InAppBrowser[],
    privacyText: '',
    features: {
      fcmDetailEnabled: false,
      fcmUserDetailEnabled: false,
      captchaEnabled: false,
      newsFeedEditAndDeleteEnabled: false,
      videoCallEnabled: false,
      isGroupMembersEnabled: true,
      isGroupMembersVideoCallEnabled: true,
      groupWithMembers: true,
      registerEnabled: false,
      passwordChangeEnabled: false,
      passwordResetEnabled: false,
      otrEnabled: false,
      mfaEnabled: false,
      notificationSound: true,
      showUnpermittedFeatures: false,
      createTeam: true,
      createChannel: true,
      isShowAllTeamsForConference: false,
      showPhoneInfo: false,
      isPrivacyTextEnabled: false,
    },
  })

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasSystemUpdatePermission = isFlagSetIn(
    [SettingsPermissions.UPDATE_SYSTEM_SETTINGS],
    permissions.settings,
  )

  useEffect(() => {
    fetchSystemSettings()
  }, [])

  const { logoUrl: logoUrl, logoRef: logoRef } = useLogoUrl(systemSettingsSlice?.imageUrlLogo)
  const { logoUrl: faviconUrl, logoRef: faviconRef } = useLogoUrl(systemSettingsSlice?.imageUrlFavicon)

  const fetchSystemSettings = async () => {
    try {
      Loading.ref.show()
      const response = await getSystemSettings({})
      if (response?.data?.data && Object.keys(response.data.data).length !== 0) {
        setSystemSettings(response.data.data)
        setSystemSettingId(response.data.data._id)
        dispatch(systemActions.updateSystem(response.data.data))
      } else {
        setSystemSettings(systemSettingsSlice)
        setNameAndFavicon(
          systemSettingsSlice.name,
          systemSettingsSlice.imageUrlFavicon,
          systemSettingsSlice.imageUrlLogo,
        )
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }

  const onSubmitHandler = async () => {
    try {
      const { isValid, errorText: nameErrorText } = requiredValidation(systemSettings.name)
      const { isValid: iosIsValid, errorText: iosErrorText } = mobileVersionValidation(
        systemSettings.iosVersion,
      )
      const { isValid: androidIsValid, errorText: androidErrorText } = mobileVersionValidation(
        systemSettings.androidVersion,
      )
      setErrorText({
        nameErrorText: nameErrorText,
        iosVersionErrorText: iosErrorText,
        androidVersionErrorText: androidErrorText,
      })

  

      if (!isValid || !iosIsValid || !androidIsValid) return
      Loading.ref.show()
      if (systemSettingId === '') {
        await createNewSystem({ ...systemSettings })
      } else {

        const system: FixMeLater = await updateSystem({
          ...{
            ...systemSettings,
            inAppBrowsers: systemSettings?.inAppBrowsers,
            name: systemSettings.name?.trim(),
            licence: systemSettings.licence?.trim(),
            apiKey: systemSettings.apiKey?.trim(),
            inputMaxChar: systemSettings.inputMaxChar,
          },
          _id: systemSettingId,
        })

        if (system?.data?.success) {
          setErrorText({
            iosVersionErrorText: '',
            androidVersionErrorText: '',
            nameErrorText: '',
          })
          setSystemSettings({ ...systemSettings, inAppBrowsers: systemSettings?.inAppBrowsers })
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setNameAndFavicon(
        systemSettings.name,
        systemSettings.imageUrlFavicon,
        systemSettings.imageUrlLogo,
      )

      dispatch(systemActions.updateSystem(systemSettings))
      const { data: configData } = (await config({})) as FixMeLater
      await setDataToDB('vimesoft', 'config', configData)
      if (Object.keys(existOtrRooms).length && !systemSettings.features.otrEnabled) {
        Object.keys(existOtrRooms).forEach((roomId: string) => {
          dispatch(otrActions.deleteOtrRoom({ roomId }))
          closeOtr({ roomId })
        })
      }
      Loading.ref.hide()
    }
  }

  const onChangeLogo = async (fileUrl: string) => {
    setSystemSettings({ ...systemSettings, imageUrlLogo: fileUrl })
  }

  const onChangeInAppBrowserLogo = async (fileUrl: string, index: number) => {
    setSystemSettings((prevSettings) => ({
      ...prevSettings,
      inAppBrowsers: prevSettings.inAppBrowsers.map((browser, browserIndex) =>
        browserIndex === index ? { ...browser, inAppBrowserLogo: fileUrl } : browser,
      ),
    }))
  }

  const onChangeFavicon = async (fileUrl: string) => {
    setSystemSettings({ ...systemSettings, imageUrlFavicon: fileUrl })
  }
  const onChangeInput = (value: string, key: string) => {
    const newValue = key === 'inputMaxChar' ? parseInt(value, 10) : value
    setSystemSettings((prevSettings) => ({
      ...prevSettings,
      [key]: newValue,
    }))
  }

  const onChangeFileExtension = (e: boolean, value: string) => {
    if (!e) {
      setSystemSettings((prevSettings) => ({
        ...prevSettings,
        fileExtensions: [...(prevSettings.fileExtensions || []), value],
      }))
    } else {
      setSystemSettings((prevSettings) => ({
        ...prevSettings,
        fileExtensions: prevSettings.fileExtensions
          ? prevSettings.fileExtensions.filter((m) => m !== value)
          : [],
      }))
    }
  }

  const handleFeatureToggle = (featureKey: keyof SystemSettings['features']) => {
    setSystemSettings((prevSettings) => ({
      ...prevSettings,
      features: {
        ...prevSettings.features,
        [featureKey]: !(prevSettings.features?.[featureKey] ?? false),
      },
    }))
  }

  const FeatureCheckbox: React.FC<FeatureCheckboxProps> = ({
    title,
    featureKey,
    label,
    explanation,
  }) => (
    <StyledFeatureItem>
      <Checkbox
        checked={systemSettings?.features?.[featureKey] ?? false}
        onChange={() => handleFeatureToggle(featureKey)}
      />
      <StyledSpecName title={explanation}>{title}</StyledSpecName>
      <FeatureLabel>{label}</FeatureLabel>
    </StyledFeatureItem>
  )

  const setNameAndFavicon = (name: string, url: string, logo: string) => {
    document.title = name
  }

  const handleInAppBrowserChange = (index: number, field: keyof InAppBrowser, value: string | boolean) => {
    setSystemSettings((prevSettings) => {
      let updatedInAppBrowsers = prevSettings.inAppBrowsers ? [...prevSettings.inAppBrowsers] : []

      if (field === "isExtraTab" && value) {
        updatedInAppBrowsers = updatedInAppBrowsers.map((browser) => ({
          ...browser,
          isExtraTab: false,
        }))
      }
      if (field ==="isActive" && !value) {//durumu passive ise extra tab olarak da kullanılamaz.
        updatedInAppBrowsers[index] = { ...updatedInAppBrowsers[index], isExtraTab: false }
      }

      updatedInAppBrowsers[index] = { ...updatedInAppBrowsers[index], [field]: value }

      return { ...prevSettings, inAppBrowsers: updatedInAppBrowsers }
    })
  }

  const addInAppBrowser = () => {
    setSystemSettings((prevSettings) => ({
      ...prevSettings,
      inAppBrowsers: [
        ...(prevSettings.inAppBrowsers || []),
        { name: '', url: '', isActive: false, isExtraTab: false, inAppBrowserLogo: '', _id: '' },
      ],
    }))
  }

  const removeInAppBrowser = (index: number) => {
    setSystemSettings((prevSettings) => ({
      ...prevSettings,
      inAppBrowsers: prevSettings.inAppBrowsers
        ? prevSettings.inAppBrowsers.filter((_, i) => i !== index)
        : [],
    }))
  }

  return (
    <Wrapper>
      <TopContainer>
        <SettingsHeader title={t(NAVIGATION.SYSTEM_SETTINGS)} />
        <CustomText typography="header3">{t(OTHERS.LICENCE_EXPIRY)}</CustomText>
      </TopContainer>
      <Layout>
        <Column>
          <ImageInputWrapper>
            <UploadImage
              type="favicon"
              title={'Favicon'}
              avatar={faviconUrl || icons.defaultGroupIcon.src}
              buttonText={BUTTONS.UPLOAD}
              onUploadPhoto={onChangeFavicon}
            />
            <UploadImage
              type="logo"
              title={'Logo'}
              avatar={logoUrl || icons.defaultGroupIcon.src }
              buttonText={BUTTONS.UPLOAD}
              onUploadPhoto={onChangeLogo}
            />
          </ImageInputWrapper>
          <CustomFormInput
            title={t(FORM.SYSTEM_NAME) as string}
            placeholder={t(PLACEHOLDERS.SYSTEM_NAME) as string}
            errorText={errorText.nameErrorText}
            onChange={(value: string) => onChangeInput(value, 'name')}
            value={systemSettings.name}
            type="text"
          />
          <CustomFormInput
            title={t(FORM.LICENCE) as string}
            onChange={(value: string) => onChangeInput(value, 'licence')}
            type="text"
            value={systemSettings.licence}
          />
          <CustomFormInput
            title={t(FORM.API_KEY) as string}
            onChange={(value: string) => onChangeInput(value, 'apiKey')}
            type="text"
            value={systemSettings.apiKey}
          />
          <CustomFormInput
            title={t(FORM.IOS_VERSION) as string}
            onChange={(value: string) => onChangeInput(value, 'iosVersion')}
            type="text"
            value={systemSettings.iosVersion}
            placeholder={`1.1.0 - (${t(PLACEHOLDERS.DEFAULT_VALUE)})`}
            errorText={errorText.iosVersionErrorText}
            errorColor="red"
          />
          <CustomFormInput
            title={t(FORM.ANDROID_VERSION) as string}
            onChange={(value: string) => onChangeInput(value, 'androidVersion')}
            type="text"
            value={systemSettings.androidVersion}
            placeholder={`1.1.0 - (${t(PLACEHOLDERS.DEFAULT_VALUE)})`}
            errorColor="red"
            errorText={errorText.androidVersionErrorText}
          />
          <CustomFormInput
            title={t(FORM.MAX_CHAR_INPUT)}
            placeholder={t(FORM.MAX_CHAR_INPUT_PLACEHOLDER)}
            value={systemSettings.inputMaxChar?.toString()}
            onChange={(value: string) => onChangeInput(value, 'inputMaxChar')}
            type="number"
          />
          <CustomFormInput
            title={t(FORM.IN_APP_BORWSER_SETTINGS_TITLE)}
            placeholder="Enter In App Browser Title (Mobile)"
            value={systemSettings.inAppBrowserTitle?.toString()}
            onChange={(value: string) => onChangeInput(value, 'inAppBrowserTitle')}
            type="text"
          />
          <PanelBar>
            <PanelBarItem title={t(FORM.IN_APP_BORWSER_SETTINGS)} className="PanelBarCustomClass">
              <InAppBrowserSettings
                browsers={systemSettings?.inAppBrowsers || []}
                onAdd={addInAppBrowser}
                onRemove={removeInAppBrowser}
                onChange={handleInAppBrowserChange}
                onChangeLogo={onChangeInAppBrowserLogo}
              />
            </PanelBarItem>
          </PanelBar>
          <br />
          <PanelBar>
            <PanelBarItem title={t(FORM.KVKK)}>
              {/* //TODO: TextArea yerine metin düzenlenebilen (bold, italic, underline, etc.) bir componenet olacak. */}
              <TextArea
                value={systemSettings.privacyText}
                onChange={(e) => onChangeInput(e.value, 'privacyText')}
                rows={20}
                style={{ height: '100%', width: '100%', padding: '10px' }}
                placeholder={t(PLACEHOLDERS.KVKK_PLACEHOLDER)}
              />
            </PanelBarItem>
          </PanelBar>

          <StyledSettingsHeader>{t(FORM.SYSTEM_SETTINGS)}</StyledSettingsHeader>
          <PanelBar>
            <PanelBarItem title={t(FORM.SYSTEM_SETTINGS)} className="PanelBarCustomClass">
              <Tooltip
                content={
                  <StyledFeatureContainer>
                    <FeatureCheckbox
                      title={t(FORM.CAPTCHA)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CAPTCHA_CHECKBOX)}
                      featureKey="captchaEnabled"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.PASSWORD_CHANGE)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CHANGE_PASSWORD_CHECKBOX)}
                      featureKey="passwordChangeEnabled"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.FORGOT_PASSWORD)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_FORGOT_PASSWORD_CHECKBOX)}
                      featureKey="passwordResetEnabled"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.OTR)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_OTR_CHECKBOX)}
                      featureKey="otrEnabled"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.NOTIFICATION_SOUND)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_NOTIFICATION_SOUND_CHECKBOX)}
                      featureKey="notificationSound"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.SHOW_ALL_TEAMS_FOR_CREATE_MEET)}
                      explanation={t(
                        EXPLANATIONS.SYSTEM_SETTINGS_SHOW_ALL_TEAMS_FOR_VIDEOCALL_CHECKBOX,
                      )}
                      featureKey="isShowAllTeamsForConference"
                      label="Web"
                    />
                    {/* <FeatureCheckbox
                      title={t(FORM.REGISTER_ENABLED)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_ENABLE_USER_REGISTER_CHECKBOX)}
                      featureKey="registerEnabled"
                      label="Web"
                    /> */}
                    <FeatureCheckbox
                      title={t(FORM.GROUP_MEMBERS_VIDEO_CALL_ENABLE)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_USER_GROUPS_VIDEO_CALL_BUTTON_CHECKBOX)}
                      featureKey="isGroupMembersVideoCallEnabled"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.GROUP_MEMBERS_ENABLE)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_USER_GROUPS_BUTTON_CHECKBOX)}
                      featureKey="isGroupMembersEnabled"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.GROUP_WITH_MEMBERS)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_SHOW_MEMBERSHIP_CHECKBOX)}
                      featureKey="groupWithMembers"
                      label="Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.KVKK)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_KVKK_CHECKBOX)}
                      featureKey="isPrivacyTextEnabled"
                      label="Mobile/Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.FCM_DETAIL)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_NOTIFICATION_DETAILS_CHECKBOX)}
                      featureKey="fcmDetailEnabled"
                      label="Mobile/Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.FCM_USER_DETAIL)}
                      explanation={t(
                        EXPLANATIONS.SYSTEM_SETTINGS_NOTIFICATION_USER_DETAILS_CHECKBOX,
                      )}
                      featureKey="fcmUserDetailEnabled"
                      label="Mobile/Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.MFA_ENABLED)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_ENABLE_MFA_CHECKBOX)}
                      featureKey="mfaEnabled"
                      label="Mobile/Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.SHOW_UNPERMITTED_FEATURES)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_UNPERMITTED_FEATURES_CHECKBOX)}
                      featureKey="showUnpermittedFeatures"
                      label="Mobile/Web"
                    />
                    <FeatureCheckbox
                      title={t(FORM.VIDEO_CALL_ENABLE)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_VIDEO_CALL_CHECKBOX)}
                      featureKey="videoCallEnabled"
                      label="Mobile"
                    />
                    <FeatureCheckbox
                      title={t(FORM.NEWS_FEED_DELETE_EDIT)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_EDIT_REMOVE_NEWSFEED_CHECKBOX)}
                      featureKey="newsFeedEditAndDeleteEnabled"
                      label="Mobile"
                    />
                    <FeatureCheckbox
                      title={t(FORM.CREATE_TEAM_ENABLED)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CREATE_TEAM_CHECKBOX)}
                      featureKey="createTeam"
                      label="Mobile"
                    />
                    <FeatureCheckbox
                      title={t(FORM.CREATE_CHANNEL_ENABLED)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CREATE_CHANNEL_CHECKBOX)}
                      featureKey="createChannel"
                      label="Mobile"
                    />
                    <FeatureCheckbox
                      title={t(FORM.SHOW_PHONE_INFO)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_SHOW_PHONE_INFO)}
                      featureKey="showPhoneInfo"
                      label="Mobile"
                    />
                  </StyledFeatureContainer>
                }
                anchorElement="target"
                openDelay={250}
                parentTitle={true}
                position="bottom"
              />
            </PanelBarItem>
          </PanelBar>

          <StyledSettingsHeader>{t(FORM.FILE_UPLOAD_TITLE)}</StyledSettingsHeader>
          <PanelBar>
            {SUPPORTED_FILE_TYPES.map((category, cIndex) => (
              <PanelBarItem title={t(category.name)} key={category.name + cIndex}>
                <StyledMimeTypeContainer>
                  {category.extensions.map((mimeType, index) => (
                    <StyledMimeTypeItem key={mimeType.value + index}>
                      <StyledCheckbox>
                        <Checkbox
                          checked={systemSettings.fileExtensions?.some(
                            (ex) => ex === mimeType.value,
                          )}
                          onChange={(e) => onChangeFileExtension(e, mimeType.value)}
                        ></Checkbox>
                      </StyledCheckbox>
                      <StyledSpecName> {mimeType.name} </StyledSpecName>
                    </StyledMimeTypeItem>
                  ))}
                </StyledMimeTypeContainer>
              </PanelBarItem>
            ))}
          </PanelBar>
        </Column>
        <CustomText typography="header3">{`${t(FORM.SYSTEM_VERSION)} : ${
          systemSettingsSlice.version
        }`}</CustomText>
      </Layout>
      <VimeButtonWrapper>
        {hasSystemUpdatePermission ? (
          <CustomButton text={t(BUTTONS.SAVE)} textColor="white" onClick={onSubmitHandler} />
        ) : null}
      </VimeButtonWrapper>
    </Wrapper>
  )
}

export default SystemManagement

const StyledMimeTypeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const StyledMimeTypeItem = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  min-width: 100px;
`

const StyledFeatureItem = styled.label`
  display: flex;
  background: rgb(248 248 248);
  padding: 7px 10px;
  -webkit-box-align: center;
  align-items: center;
  width: 100%;
`
const FeatureLabel = styled.span`
  border-radius: 5px;
  margin-left: 10px;
  font-size: 11px;
  color: white;
  font-weight: bold;
  background: #119fdb;
  padding: 5px 12px;
`

const StyledSpecName = styled.span`
  cursor: pointer;
  flex: 1 1 0%;
  text-align: left;
  padding-right: 13px;
  padding-left: 14px;
  font-weight: 500;
  margin-top: -3px;
`

const StyledCheckbox = styled.div`
  display: flex;
  margin-right: 11px;
  margin-top: 6px;
`
const StyledFeatureContainer = styled.div`
  display: flex;
  flex-direction: column; // Dikey sıralama için
  align-items: flex-start; // Öğeleri sola hizala
  gap: 10px; // Öğeler arasında dikey boşluk
  margin-bottom: 20px; // Altına boşluk ekle
`

const StyledSettingsHeader = styled.h2`
  text-align: left;
  font-size: 16px;
  margin-bottom: 20px;
  margin-top: 25px;
`

const Wrapper = styled.div`
  padding: 0px 30px;
  width: 100%;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Layout = styled.div`
  min-height: calc(100vh - 200px);
  overflow-x: auto;
  padding: 20px;
  border: 1px solid ${colors.light80};
`

const Column = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const VimeButtonWrapper = styled.div`
  padding: 4px;
  float: right;
  margin-top: 20px;
`

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: flex-end;
  margin-bottom: 10px;
`

const ImageInputWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 24px;
  margin-bottom: 24px;
  margin-top: 10px;
  width: 100%;
  @media (max-width: 665px) {
    flex-direction: column;
    align-items: center;
  }
`
